/* eslint-disable prefer-arrow-functions/prefer-arrow-functions */
import React from "react";
import Link from "next/link";
import Image from "next/image";
import type { ApiOrderDetail } from "../types";

interface OrderItemProps {
  item: ApiOrderDetail;
  productHref?: string;
}

function getVariantLabel(item: ApiOrderDetail): string | undefined {
  const variants = item.orderProductsVariants;
  if (!variants?.length) return undefined;
  return (
    variants
      .map((v) =>
        `${v.companyProductVariantId.groupId?.name ?? ""}: ${v.companyProductVariantId.variantId?.value ?? ""}`.trim(),
      )
      .filter(Boolean)
      .join(", ") || undefined
  );
}

export function OrderItem({ item, productHref }: OrderItemProps) {
  const imageUrl =
    item.productId?.images?.[0] ??
    "https://placehold.co/80x80/f1f5f9/64748b?text=Product";
  const name = item.productId?.name ?? "Unknown Product";
  const variantLabel = getVariantLabel(item);

  const content = (
    <>
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-50 p-1">
        <Image
          src={imageUrl}
          alt={name}
          fill
          className="object-contain transition-transform duration-300 group-hover/item:scale-110"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="line-clamp-2 text-sm font-medium leading-tight text-gray-800 group-hover/item:text-black">
          {name}
        </p>
        {variantLabel && (
          <p className="text-xs text-gray-500 line-clamp-1">{variantLabel}</p>
        )}
        <div className="flex items-center gap-2">
          {item.quantity > 1 && (
            <span className="text-xs text-gray-400">Qty: {item.quantity}</span>
          )}
          {item.quantity <= 1 && (
            <span className="text-xs text-gray-400">Qty: 1</span>
          )}
        </div>
        <p className="text-sm font-bold text-primary">
          ${item.totalPrice.toFixed(2)}
        </p>
      </div>
    </>
  );

  const wrapperClass =
    "group/item flex min-w-[220px] max-w-[280px] flex-shrink-0 items-center gap-3 rounded-xl border-2 border-gray-200 bg-white p-3 transition-all duration-200 cursor-pointer hover:border-primary/30 hover:shadow-md hover:shadow-primary/5";

  if (productHref) {
    return (
      <Link href={productHref} className={wrapperClass}>
        {content}
      </Link>
    );
  }

  return <div className={wrapperClass}>{content}</div>;
}
